/**
 * Reports what the desktop app shows to the backend (plan 13 Part 2), so an
 * agent's tools can say where a frame is shown: Focus (each frame's id, who
 * showed it, and for an agent frame its owner and round, the main frame
 * first), the frames floating in their own windows and the frames pinned to
 * the desktop, each with its project and display. The desktop owner window
 * reports for every display; mirrors share its presentation. Frames go by
 * id alone: the owner's frames store holds only what its own wall paged
 * in, and the backend adds each frame's title. A burst of changes sends one
 * report; an unchanged report is sent again hourly, so the backend's copy
 * never expires while the app runs.
 */
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import type { PinnedWidgetSize } from "@/components/shapes/shared/widget-sizing";
import {
  readDesktopPins,
  subscribeDesktopPins,
} from "@/desktop/pin/desktop-pins-store";
import { useFloatingFrameStore } from "@/desktop/floating-frame-store";
import { isUserKept } from "@/lib/focus/focus-plan-rounds";
import type { PresentationState } from "@/lib/focus/focus-types";
import { isTauri } from "@/lib/is-tauri";
import { useCompanionStore } from "@/stores/companion-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";

export const FOCUS_STATE_REPORT_DELAY_MS = 250;
/** Well inside the backend's six-hour keep (`focus_state_services.py`). */
export const FOCUS_STATE_REFRESH_MS = 60 * 60 * 1000;

export interface FocusStateFrame {
  id: string;
  by: "agent" | "user";
  owner?: string;
  round?: string;
  active: boolean;
}

/** A frame shown outside Focus: in its own window or pinned to the desktop. */
interface ScreenPlace {
  id: string;
  project_id: string;
  display_id: number | null;
}

interface DesktopPinPlace extends ScreenPlace {
  size: PinnedWidgetSize;
  width: number;
  height: number;
}

interface FocusStateReport {
  project_id: string;
  display_id: number | null;
  frames: FocusStateFrame[];
  floating: ScreenPlace[];
  pins: DesktopPinPlace[];
}

/** The frames a focus presentation shows, the main frame first. */
export function focusStateFrames(
  presentation: PresentationState | null,
): FocusStateFrame[] {
  if (presentation?.variant !== "focus") return [];
  const activeId = presentation.frameIds[presentation.activeIndex];
  return presentation.frameIds.map((id) => {
    const tag = presentation.frameTags?.[id];
    const byUser = isUserKept(tag);
    return {
      id,
      by: byUser ? "user" : "agent",
      ...(!byUser && tag?.owner && { owner: tag.owner }),
      ...(!byUser && tag?.round && { round: tag.round }),
      active: id === activeId,
    };
  });
}

function currentReport(): FocusStateReport | null {
  const projectId = useProjectStore.getState().currentProject?.id;
  if (!projectId) return null;
  const presentation = usePresentationStore.getState().presentation;
  return {
    project_id: projectId,
    display_id:
      presentation?.variant === "focus"
        ? (presentation.displayId ?? null)
        : null,
    frames: focusStateFrames(presentation),
    floating: Object.values(useFloatingFrameStore.getState().frames).map(
      (frame) => ({
        id: frame.frameId,
        project_id: frame.projectId,
        display_id: frame.displayId,
      }),
    ),
    pins: readDesktopPins().map((pin) => ({
      id: pin.frameId,
      project_id: pin.projectId,
      display_id: pin.displayId,
      size: pin.size,
      width: pin.rect.width,
      height: pin.rect.height,
    })),
  };
}

/** Send the focus state whenever it changes or the socket (re)connects; returns the stop. */
export function startFocusStateReports(): () => void {
  // Only the desktop app shows Focus over the user's screen.
  if (!isTauri()) return () => {};
  let sent = "";
  let timer: ReturnType<typeof setTimeout> | null = null;
  const send = () => {
    timer = null;
    const next = currentReport();
    const ws = getCurrentDenkerWs();
    const connected =
      useCompanionStore.getState().wsConnectionState === "connected";
    if (!next || !ws || !connected) return;
    const signature = JSON.stringify(next);
    if (signature === sent) return;
    sent = signature;
    ws.sendRaw("focus.state", { ...next });
  };
  const schedule = () => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(send, FOCUS_STATE_REPORT_DELAY_MS);
  };
  const resend = () => {
    sent = "";
    schedule();
  };
  const stops = [
    usePresentationStore.subscribe((state, previous) => {
      if (state.presentation !== previous.presentation) schedule();
    }),
    useProjectStore.subscribe((state, previous) => {
      if (state.currentProject?.id !== previous.currentProject?.id) schedule();
    }),
    useCompanionStore.subscribe((state, previous) => {
      if (
        state.wsConnectionState === "connected" &&
        previous.wsConnectionState !== "connected"
      ) {
        resend();
      }
    }),
    useFloatingFrameStore.subscribe((state, previous) => {
      if (state.frames !== previous.frames) schedule();
    }),
    subscribeDesktopPins(schedule),
  ];
  const refresh = setInterval(resend, FOCUS_STATE_REFRESH_MS);
  return () => {
    if (timer !== null) clearTimeout(timer);
    clearInterval(refresh);
    for (const stop of stops) stop();
  };
}
